// ─── IntegrationsSection.jsx ─────────────────────────────────────────────────
// Extracted component for Settings page. Owner-only, Pro+ tier gate.
// Handles: OAuth connect, property/site selection, manual sync, disconnect.

import { useState, useEffect } from "react";
import { integrations as integrationsApi } from "../lib/supabase.js";

const PROVIDERS = {
  ga4: {
    name: "Google Analytics 4",
    icon: "📈",
    description: "Website traffic, user behavior, and conversion data",
    propertyLabel: "GA4 Property",
  },
  search_console: {
    name: "Search Console",
    icon: "🔍",
    description: "Organic search queries, impressions, clicks, and rankings",
    propertyLabel: "Site",
  },
};

export default function IntegrationsSection({ color, account }) {
  const [integrationsList, setIntegrationsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null); // provider key being connected
  const [syncing, setSyncing] = useState(null);
  const [message, setMessage] = useState(null);

  const sectionStyle = { background: "#111118", border: "1px solid #1e1e2e", borderRadius: 10, padding: 24, marginBottom: 16 };
  const labelStyle = { display: "block", fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, fontWeight: 700 };

  const tierAllowed = account?.plan === "enterprise" || account?.plan === "pro";

  const loadIntegrations = async () => {
    setLoading(true);
    try {
      const result = await integrationsApi.list();
      setIntegrationsList(result.integrations || []);
    } catch (err) {
      console.error("Failed to load integrations:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadIntegrations();

    // Check URL params for OAuth callback results
    const params = new URLSearchParams(window.location.search);
    if (params.get("integration_connected")) {
      const provider = params.get("integration_connected");
      const needsSelection = params.get("needs_selection") === "true";
      setMessage({
        type: "success",
        text: needsSelection
          ? `${PROVIDERS[provider]?.name || provider} connected! Select a property below.`
          : `${PROVIDERS[provider]?.name || provider} connected successfully.`,
      });
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      loadIntegrations();
    }
    if (params.get("integration_error")) {
      setMessage({ type: "error", text: `Connection failed: ${params.get("integration_error")}` });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleConnect = async (provider) => {
    setConnecting(provider);
    setMessage(null);
    try {
      const result = await integrationsApi.getOAuthUrl(provider);
      if (result.error) throw new Error(result.error);
      if (result.url) {
        window.location.href = result.url; // redirect to Google consent
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    const name = PROVIDERS[provider]?.name || provider;
    if (!confirm(`Disconnect ${name}? Synced data will remain in your analytics.`)) return;
    try {
      const result = await integrationsApi.disconnect(provider);
      if (result.error) throw new Error(result.error);
      setMessage({ type: "success", text: `${name} disconnected.` });
      loadIntegrations();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const handlePropertySelect = async (provider, propertyId, metadata) => {
    try {
      const result = await integrationsApi.update(provider, propertyId, metadata);
      if (result.error) throw new Error(result.error);
      setMessage({ type: "success", text: "Property selected. You can now sync data." });
      loadIntegrations();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const handleSync = async (provider) => {
    setSyncing(provider);
    setMessage(null);
    try {
      const result = await integrationsApi.sync(provider, 30);
      if (result.error) throw new Error(result.error);
      setMessage({ type: "success", text: `${PROVIDERS[provider]?.name}: synced ${result.rows_synced} data points (${result.date_range.start} → ${result.date_range.end}).` });
      loadIntegrations();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
    setSyncing(null);
  };

  const getIntegration = (provider) => integrationsList.find(i => i.provider === provider);

  return (
    <div style={sectionStyle}>
      <div style={{ fontSize: 12, color: "#666", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 18 }}>🔗 External Integrations</div>

      {!tierAllowed ? (
        <div style={{ padding: "14px 16px", background: "#0a0a10", borderRadius: 8, border: "1px solid #1a1a2a" }}>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>Integrations are available on Pro and Enterprise plans.</div>
          <div style={{ fontSize: 11, color: "#555" }}>Connect Google Analytics, Search Console, and more to see external data in your Analytics dashboard.</div>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 11, color: "#444", margin: "0 0 16px", lineHeight: 1.5 }}>
            Connect external analytics services. Data syncs into your Analytics dashboard alongside Clarifico first-party data.
          </p>

          {message && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 12,
              background: message.type === "error" ? "#2a0a0a" : "#0a2e1a",
              color: message.type === "error" ? "#f87171" : "#22c55e",
              border: `1px solid ${message.type === "error" ? "#991b1b" : "#166534"}`,
            }}>{message.text}</div>
          )}

          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: "#444", fontSize: 12 }}>Loading integrations...</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {Object.entries(PROVIDERS).map(([provKey, prov]) => {
                const integration = getIntegration(provKey);
                const isConnected = integration?.status === "connected";
                const isError = integration?.status === "error" || integration?.status === "token_expired";
                const needsProperty = isConnected && !integration.provider_account_id;
                const isSyncing = syncing === provKey;

                return (
                  <div key={provKey} style={{
                    padding: "16px 18px", background: "#0a0a10", borderRadius: 8,
                    border: `1px solid ${isConnected ? "#16653440" : isError ? "#991b1b40" : "#1a1a2a"}`,
                  }}>
                    {/* Header row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isConnected || isError ? 12 : 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 20 }}>{prov.icon}</span>
                        <div>
                          <div style={{ fontSize: 13, color: "#ccc", fontWeight: 600 }}>{prov.name}</div>
                          <div style={{ fontSize: 11, color: "#555" }}>{prov.description}</div>
                        </div>
                      </div>

                      {isConnected ? (
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700, textTransform: "uppercase", background: "#0a2e1a", color: "#22c55e" }}>Connected</span>
                          <button
                            onClick={() => handleDisconnect(provKey)}
                            style={{ padding: "4px 10px", background: "none", border: "1px solid #1e1e2e", borderRadius: 6, color: "#555", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
                          >Disconnect</button>
                        </div>
                      ) : isError ? (
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700, textTransform: "uppercase", background: "#2a0a0a", color: "#f87171" }}>
                            {integration.status === "token_expired" ? "Token Expired" : "Error"}
                          </span>
                          <button
                            onClick={() => handleConnect(provKey)}
                            disabled={connecting === provKey}
                            style={{ padding: "5px 14px", background: color, border: "none", borderRadius: 6, color: "#fff", fontSize: 11, fontWeight: 600, cursor: connecting === provKey ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: connecting === provKey ? 0.5 : 1 }}
                          >{connecting === provKey ? "Connecting..." : "Reconnect"}</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleConnect(provKey)}
                          disabled={connecting === provKey}
                          style={{ padding: "8px 18px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 600, cursor: connecting === provKey ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: connecting === provKey ? 0.5 : 1 }}
                        >{connecting === provKey ? "Redirecting..." : "Connect"}</button>
                      )}
                    </div>

                    {/* Property selection (when connected but needs selection) */}
                    {needsProperty && integration.available_properties?.length > 1 && (
                      <div style={{ padding: "12px 14px", background: "#111118", borderRadius: 8, border: "1px solid #1e1e2e" }}>
                        <div style={labelStyle}>Select {prov.propertyLabel}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {integration.available_properties.map(prop => (
                            <button
                              key={prop.id}
                              onClick={() => handlePropertySelect(provKey, prop.id, { name: prop.name, account_name: prop.account_name })}
                              style={{
                                padding: "8px 12px", background: "#0a0a10", border: "1px solid #1e1e2e",
                                borderRadius: 6, color: "#ccc", fontSize: 12, cursor: "pointer",
                                fontFamily: "inherit", textAlign: "left", display: "flex", justifyContent: "space-between",
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{prop.name}</span>
                              {prop.account_name && <span style={{ color: "#555", fontSize: 11 }}>{prop.account_name}</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Connected info + sync controls */}
                    {isConnected && integration.provider_account_id && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 11, color: "#555" }}>
                          <span style={{ color: "#888", fontWeight: 600 }}>
                            {integration.provider_metadata?.name || integration.provider_account_id}
                          </span>
                          {integration.last_sync_at && (
                            <span style={{ marginLeft: 12 }}>
                              Last sync: {new Date(integration.last_sync_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleSync(provKey)}
                          disabled={isSyncing}
                          style={{
                            padding: "6px 14px", background: "#111118",
                            border: `1px solid ${color}30`, borderRadius: 6,
                            color, fontSize: 11, fontWeight: 600,
                            cursor: isSyncing ? "not-allowed" : "pointer",
                            fontFamily: "inherit", opacity: isSyncing ? 0.5 : 1,
                          }}
                        >{isSyncing ? "Syncing..." : "Sync Now"}</button>
                      </div>
                    )}

                    {/* Error details */}
                    {isError && integration.last_sync_error && (
                      <div style={{ fontSize: 11, color: "#f87171", marginTop: 4 }}>
                        Error: {integration.last_sync_error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
